import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useBoardOverlayFrame } from "./useBoardOverlayFrame";
import { boardFrameClipPath } from "./boardFrameClipPath";

const GUIDE_COLOR = "#E11D8F";

const guidesEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every(
    (guide, index) =>
      guide.axis === right[index].axis &&
      guide.position === right[index].position &&
      guide.start === right[index].start &&
      guide.end === right[index].end
  );

const AlignmentGuidesOverlay = forwardRef(
  ({ boardRef, viewportRef, syncKey, clipToCanvas = false }, ref) => {
    const overlayRef = useRef(null);
    const frame = useBoardOverlayFrame({
      boardRef,
      overlayRef,
      viewportRef,
      syncKey,
    });
    const clipPath = clipToCanvas ? boardFrameClipPath(frame) : undefined;
    const [guides, setGuides] = useState([]);

    useImperativeHandle(
      ref,
      () => ({
        setGuides: (next = []) => {
          setGuides((current) => (guidesEqual(current, next) ? current : next));
        },
        clear: () => {
          setGuides((current) => (current.length ? [] : current));
        },
      }),
      []
    );

    return (
      <Box
        ref={overlayRef}
        data-alignment-guides-root=""
        sx={{
          position: "absolute",
          inset: 0,
          overflow: clipToCanvas ? "hidden" : "visible",
          clipPath,
          pointerEvents: "none",
          zIndex: 5,
        }}
      >
        {guides.map((guide) => {
          const length = Math.max(0, guide.end - guide.start);
          if (length < 1) return null;
          if (guide.axis === "x") {
            return (
              <Box
                key={`x-${guide.position}-${guide.start}-${guide.end}`}
                sx={{
                  position: "absolute",
                  left: frame.left + guide.position,
                  top: frame.top + guide.start,
                  width: "1px",
                  height: length,
                  bgcolor: GUIDE_COLOR,
                  transform: "translateX(-0.5px)",
                  pointerEvents: "none",
                }}
              />
            );
          }
          if (guide.axis === "y") {
            return (
              <Box
                key={`y-${guide.position}-${guide.start}-${guide.end}`}
                sx={{
                  position: "absolute",
                  left: frame.left + guide.start,
                  top: frame.top + guide.position,
                  width: length,
                  height: "1px",
                  bgcolor: GUIDE_COLOR,
                  transform: "translateY(-0.5px)",
                  pointerEvents: "none",
                }}
              />
            );
          }
          return null;
        })}
      </Box>
    );
  }
);

AlignmentGuidesOverlay.displayName = "AlignmentGuidesOverlay";

export default AlignmentGuidesOverlay;
